import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";

export interface GameReminder {
  gameId: number;
  homeTeam: string;
  awayTeam: string;
  gameTime: string;
  gameDate: string;
  league: string;
  notifyAt: number; // timestamp for when to notify
  createdAt: number;
}

const REMINDERS_KEY = "hockey_game_reminders";
const NOTIFICATION_LEAD_TIME = 15 * 60 * 1000; // 15 minutes before game

export function useGameNotifications() {
  const [reminders, setReminders] = useState<GameReminder[]>([]);
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const { toast } = useToast();

  // Load reminders from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(REMINDERS_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as GameReminder[];
        // Filter out past reminders
        const validReminders = parsed.filter(r => r.notifyAt > Date.now());
        setReminders(validReminders);
        // Clean up localStorage
        if (validReminders.length !== parsed.length) {
          localStorage.setItem(REMINDERS_KEY, JSON.stringify(validReminders));
        }
      } catch (e) {
        console.error("Error parsing reminders:", e);
      }
    }
  }, []);

  // Check notification permission
  useEffect(() => {
    if ("Notification" in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      toast({
        title: "Notifications not supported",
        description: "Your browser doesn't support notifications.",
        variant: "destructive",
      });
      return false;
    }

    if (Notification.permission === "granted") {
      setPermission("granted");
      return true;
    }

    if (Notification.permission !== "denied") {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === "granted";
    }

    toast({
      title: "Notifications blocked",
      description: "Please enable notifications in your browser settings.",
      variant: "destructive",
    });
    return false;
  }, [toast]);

  // Add a reminder
  const addReminder = useCallback(async (
    gameId: number,
    homeTeam: string,
    awayTeam: string,
    gameTime: string,
    gameDate: string,
    league: string,
    gameTimestamp: number
  ) => {
    // Request permission if not granted
    const hasPermission = await requestPermission();
    if (!hasPermission) return false;

    // Check if reminder already exists
    const existing = reminders.find(r => r.gameId === gameId);
    if (existing) {
      toast({
        title: "Already reminded",
        description: `You're already set to be reminded for ${awayTeam} @ ${homeTeam}`,
      });
      return false;
    }

    // Calculate notification time (15 min before game)
    const notifyAt = gameTimestamp - NOTIFICATION_LEAD_TIME;

    // Don't add if game is in the past or too soon
    if (notifyAt <= Date.now()) {
      toast({
        title: "Game too soon",
        description: "This game starts in less than 15 minutes.",
        variant: "destructive",
      });
      return false;
    }

    const newReminder: GameReminder = {
      gameId,
      homeTeam,
      awayTeam,
      gameTime,
      gameDate,
      league,
      notifyAt,
      createdAt: Date.now(),
    };

    const updated = [...reminders, newReminder];
    setReminders(updated);
    localStorage.setItem(REMINDERS_KEY, JSON.stringify(updated));

    toast({
      title: "Reminder set! 🔔",
      description: `We'll notify you 15 minutes before ${awayTeam} @ ${homeTeam}`,
    });

    return true;
  }, [reminders, requestPermission, toast]);

  // Remove a reminder
  const removeReminder = useCallback((gameId: number) => {
    const updated = reminders.filter(r => r.gameId !== gameId);
    setReminders(updated);
    localStorage.setItem(REMINDERS_KEY, JSON.stringify(updated));

    toast({
      title: "Reminder removed",
      description: "You won't be notified about this game.",
    });
  }, [reminders, toast]);

  // Check if a game has a reminder
  const hasReminder = useCallback((gameId: number) => {
    return reminders.some(r => r.gameId === gameId);
  }, [reminders]);

  // Check for due notifications
  useEffect(() => {
    if (permission !== "granted") return;

    const checkInterval = setInterval(() => {
      const now = Date.now();
      const dueReminders = reminders.filter(r => r.notifyAt <= now);

      dueReminders.forEach(reminder => {
        // Show browser notification
        new Notification(`🏒 ${reminder.league} Game Starting Soon!`, {
          body: `${reminder.awayTeam} @ ${reminder.homeTeam} starts at ${reminder.gameTime}`,
          icon: "/favicon.ico",
          tag: `game-${reminder.gameId}`,
          requireInteraction: true,
        });

        // Also show in-app toast
        toast({
          title: `🏒 Game Starting Soon!`,
          description: `${reminder.awayTeam} @ ${reminder.homeTeam} starts at ${reminder.gameTime}`,
        });
      });

      // Remove notified reminders
      if (dueReminders.length > 0) {
        const updatedReminders = reminders.filter(r => r.notifyAt > now);
        setReminders(updatedReminders);
        localStorage.setItem(REMINDERS_KEY, JSON.stringify(updatedReminders));
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(checkInterval);
  }, [reminders, permission, toast]);

  return {
    reminders,
    permission,
    requestPermission,
    addReminder,
    removeReminder,
    hasReminder,
    isSupported: "Notification" in window,
  };
}
